import React, { useEffect, useState } from "react";
import { api, formatINR } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Coins, Save, ToggleLeft, ToggleRight } from "lucide-react";

const fmtDate = (s) => (s ? new Date(s).toLocaleString() : "—");

const AdminCoins = () => {
  const [settings, setSettings] = useState(null);
  const [txns, setTxns] = useState([]);
  const [saving, setSaving] = useState(false);
  const [adjust, setAdjust] = useState({ customer_id: "", delta: 0, note: "" });
  const [adjusting, setAdjusting] = useState(false);

  const load = async () => {
    const [s, t] = await Promise.all([
      api.get("/admin/coins/settings").then(r => r.data).catch(() => null),
      api.get("/admin/coins/transactions?limit=200").then(r => r.data).catch(() => []),
    ]);
    setSettings(s);
    setTxns(t);
  };

  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch("/admin/coins/settings", settings);
      toast.success("Coin settings saved");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const submitAdjust = async () => {
    if (!adjust.customer_id || !adjust.delta) { toast.error("Customer ID and delta required"); return; }
    setAdjusting(true);
    try {
      await api.post(`/admin/customers/${adjust.customer_id}/coins/adjust`, {
        delta: parseInt(adjust.delta, 10),
        note: adjust.note || "Manual admin adjustment",
      });
      toast.success("Adjusted");
      setAdjust({ customer_id: "", delta: 0, note: "" });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setAdjusting(false); }
  };

  const set = (k) => (e) => setSettings({ ...settings, [k]: e.target.type === "number" ? parseFloat(e.target.value || 0) : e.target.value });

  if (!settings) return <p className="text-cream/75">Loading…</p>;

  return (
    <div data-testid="admin-coins-page">
      <div className="flex items-end justify-between mb-6 flex-wrap gap-4">
        <div>
          <p className="eyebrow text-cream/65 mb-1">Loyalty</p>
          <h1 className="font-hero text-3xl text-cream flex items-center gap-3">
            <Coins size={26} /> Coin wallet
          </h1>
          <p className="text-cream/75 text-sm mt-1 max-w-xl">
            Configure how customers earn, redeem and spend their wallet coins. Coins boost retention by giving repeat customers an automatic discount on every order.
          </p>
        </div>
      </div>

      {/* Settings */}
      <section className="bg-white rounded-2xl border border-border p-6 mb-6 max-w-4xl" data-testid="coin-settings-form">
        <div className="flex items-center justify-between mb-5">
          <p className="eyebrow">Program rules</p>
          <button
            onClick={() => setSettings({ ...settings, enabled: !settings.enabled })}
            className="flex items-center gap-2 text-sm"
            data-testid="coin-enabled-toggle"
          >
            {settings.enabled
              ? <><ToggleRight size={28} className="text-emerald-600" /> Enabled</>
              : <><ToggleLeft size={28} className="text-charcoal/70" /> Disabled</>}
          </button>
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="eyebrow block mb-1">Coin name</label>
            <Input value={settings.coin_name || ""} onChange={set("coin_name")} placeholder="Rieave Coins" data-testid="coin-name-input" />
          </div>
          <div>
            <label className="eyebrow block mb-1">Earn on order status</label>
            <select
              value={settings.earn_on_status}
              onChange={set("earn_on_status")}
              className="h-10 w-full rounded-md border border-input bg-transparent px-3 text-sm"
              data-testid="coin-earn-status"
            >
              <option value="delivered">Delivered (recommended)</option>
              <option value="shipped">Shipped</option>
              <option value="processing">Processing</option>
            </select>
          </div>
          <div>
            <label className="eyebrow block mb-1">Earn rate (coins per ₹100 spent)</label>
            <Input type="number" step="0.5" value={settings.earn_rate_per_100} onChange={set("earn_rate_per_100")} data-testid="coin-earn-rate" />
            <p className="text-xs text-charcoal/70 mt-1">Currently: {settings.earn_rate_per_100}% back as coins</p>
          </div>
          <div>
            <label className="eyebrow block mb-1">Coin value at redemption (₹ per coin)</label>
            <Input type="number" step="0.1" value={settings.coin_value_inr} onChange={set("coin_value_inr")} data-testid="coin-value" />
          </div>
          <div>
            <label className="eyebrow block mb-1">Max redemption per order (% of subtotal)</label>
            <Input type="number" step="1" min="0" max="100" value={settings.max_redeem_pct} onChange={set("max_redeem_pct")} data-testid="coin-max-pct" />
            <p className="text-xs text-charcoal/70 mt-1">Customers can redeem at most this % of any order with coins.</p>
          </div>
          <div>
            <label className="eyebrow block mb-1">Welcome bonus (coins on signup)</label>
            <Input type="number" min="0" value={settings.welcome_bonus} onChange={set("welcome_bonus")} data-testid="coin-welcome" />
          </div>
        </div>

        <button onClick={save} disabled={saving} className="btn-primary mt-5 inline-flex items-center gap-2" data-testid="save-coin-settings">
          <Save size={14} /> {saving ? "Saving…" : "Save settings"}
        </button>
      </section>

      {/* Manual adjust */}
      <section className="bg-white rounded-2xl border border-border p-6 mb-6 max-w-4xl" data-testid="coin-adjust-form">
        <p className="eyebrow mb-4">Manual credit / debit</p>
        <div className="grid md:grid-cols-3 gap-3 mb-3">
          <Input
            placeholder="Customer ID (UUID)"
            value={adjust.customer_id}
            onChange={(e) => setAdjust({ ...adjust, customer_id: e.target.value })}
            data-testid="adjust-customer-id"
          />
          <Input
            type="number"
            placeholder="Delta (e.g. 50 or -20)"
            value={adjust.delta}
            onChange={(e) => setAdjust({ ...adjust, delta: e.target.value })}
            data-testid="adjust-delta"
          />
          <Input
            placeholder="Note (visible to customer)"
            value={adjust.note}
            onChange={(e) => setAdjust({ ...adjust, note: e.target.value })}
            data-testid="adjust-note"
          />
        </div>
        <button onClick={submitAdjust} disabled={adjusting} className="btn-outline" data-testid="adjust-submit">
          {adjusting ? "Adjusting…" : "Adjust balance"}
        </button>
        <p className="text-xs text-charcoal/70 mt-2">
          Tip: copy a Customer ID from the Customers page. Positive delta credits, negative debits.
        </p>
      </section>

      {/* Ledger */}
      <section className="bg-white rounded-2xl border border-border max-w-6xl overflow-hidden" data-testid="coin-ledger">
        <div className="p-5 border-b border-border">
          <p className="eyebrow">Recent transactions</p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[720px]">
            <thead>
              <tr className="text-left font-label text-[11px] uppercase tracking-[0.22em] text-charcoal/70 border-b border-border">
                <th className="py-3 px-4">When</th>
                <th>Customer</th>
                <th>Kind</th>
                <th>Note</th>
                <th>Order</th>
                <th className="text-right pr-4">Δ</th>
                <th className="text-right pr-4">Balance after</th>
              </tr>
            </thead>
            <tbody>
              {txns.length === 0 && (
                <tr><td colSpan="7" className="py-8 text-center text-charcoal/70">No transactions yet.</td></tr>
              )}
              {txns.map(t => (
                <tr key={t.id} className="border-b border-border last:border-b-0" data-testid={`coin-row-${t.id}`}>
                  <td className="py-3 px-4 text-xs">{fmtDate(t.created_at)}</td>
                  <td className="text-xs font-mono text-charcoal/70 truncate max-w-[120px]">{t.customer_id?.slice(0, 8)}…</td>
                  <td><span className="font-label text-[10px] uppercase tracking-[0.18em] px-2 py-0.5 rounded-full bg-cream">{t.kind}</span></td>
                  <td className="text-charcoal/80 text-xs max-w-[260px] truncate">{t.note}</td>
                  <td className="text-xs font-mono text-charcoal/70">{t.order_id ? t.order_id.slice(0, 8) + "…" : "—"}</td>
                  <td className={`text-right pr-4 font-cormorant text-lg ${t.delta >= 0 ? "text-emerald-700" : "text-rose"}`}>
                    {t.delta >= 0 ? "+" : ""}{t.delta}
                  </td>
                  <td className="text-right pr-4 text-charcoal">{t.balance_after}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
};

export default AdminCoins;
